import { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Image } from 'expo-image'
import { useStagesStore } from 'hooks/useStagesStore'
import Select from '@ui/forms/Select'
import Slider from '@ui/components/Slider'
import Button from '@ui/buttons/Button'
import Segmented from '@ui/elements/Segmented'
import { SegmentedOption } from '@ui/elements/Segmented/types'
import Messages from '@ui/features/measurement/Messages'

import {
  MESSAGES_CONTAINER_HEIGHT,
  MESSAGES_CONTAINER_PADDING_HORIZONTAL,
  MESSAGES_CONTAINER_PADDING_VERTICAL,
} from '@ui/features/measurement/Messages/styles'

import {
  Measurement,
  MeasurementMessage,
} from '@ui/features/measurement/Messages/types'

import OvalComponent, {
  getOvalState,
  OvalState,
} from '@ui/features/measurement/Oval'

import { Oval } from '@ui/features/measurement/Oval/types'
import Review from '@ui/features/measurement/Review'
import Instructions from '@ui/features/measurement/Instructions'
import Points from '@ui/features/measurement/Points'
import { useMeasurementLauncher } from 'features/measurements/MeasurementContext'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import ChipButton from '@ui/elements/ChipButton'
import VBOX from '@ui/features/measurement/Settings/VBOX'
import Manual from '@ui/features/measurement/Review/components/ManualAdjustment'

const messageOptions = Object.values(Measurement.Messages).map((value) => ({
  label: value,
  value,
}))

const ovalStateOptions: SegmentedOption<OvalState>[] = Object.values(
  Oval.States
).map((state) => ({
  id: state,
  label: state.charAt(0).toUpperCase() + state.slice(1),
}))

const ovalStateOptionsById = Object.fromEntries(
  ovalStateOptions.map((option) => [option.id, option])
)

const measurementOptions: SegmentedOption<'pd' | 'sh'>[] = [
  { id: 'sh', label: 'SH' },
  { id: 'pd', label: 'PD' },
]

const measurementOptionById = Object.fromEntries(
  measurementOptions.map((option) => [option.id, option])
)

const INITIAL_MESSAGE = Measurement.Messages.headUp

export default function DevMeasurementsUI() {
  const setStage = useStagesStore((state) => state.setStage)
  const { open: openMeasurement } = useMeasurementLauncher()
  const [message, setMessage] = useState<MeasurementMessage>(INITIAL_MESSAGE)
  const [progress, setProgress] = useState(0)
  const [isOpenControls, setIsOpenControls] = useState(false)
  const [isOpenReview, setIsOpenReview] = useState(false)
  const [isOpenInstructions, setIsOpenInstructions] = useState(false)
  const [isOpenVbox, setIsOpenVbox] = useState(false)
  const [vboxMm, setVboxMm] = useState<number | undefined>(45)
  const [isOpenManualProposal, setIsOpenManualProposal] = useState(false)
  const [isOpenManualContent, setIsOpenManualContent] = useState(false)
  const [measurement, setMeasurement] = useState<'pd' | 'sh'>('sh')
  const [ovalState, setOvalState] = useState<SegmentedOption<OvalState>>(
    ovalStateOptionsById[getOvalState(INITIAL_MESSAGE)]
  )

  const handleMessageChange = (value: MeasurementMessage) => {
    setMessage(value)
    setOvalState(ovalStateOptionsById[getOvalState(value)])
  }

  useEffect(() => {
    setStage(null, 'empty')
  }, [])

  const animatedReviewState = useSharedValue(0)
  const styleAnimatedReview = useAnimatedOpacity(isOpenReview)
  const styleAnimatedImage = useAnimatedStyle(() => ({
    transform: [
      { scale: interpolate(animatedReviewState.value, [0, 1], [1, 1.66]) },
      { translateY: interpolate(animatedReviewState.value, [0, 1], [0, -280]) },
    ],
  }))

  useEffect(() => {
    animatedReviewState.set(isOpenReview ? 1 : 0)
  }, [isOpenReview])

  return (
    <>
      <View className="flex-1">
        <Animated.View style={[StyleSheet.absoluteFill, styleAnimatedImage]}>
          <Image
            source={require('@/images/measurement-mockup.png')}
            style={[StyleSheet.absoluteFill]}
          />
        </Animated.View>
        <Animated.View
          style={[StyleSheet.absoluteFill, styleAnimatedReview]}
          pointerEvents="none"
        >
          <Points
            points={[
              { x: 367, y: 211, type: 'eye', variant: 'auto' },
              { x: 666, y: 211, type: 'eye', variant: 'auto' },
              { x: 367, y: 363, type: 'frame', variant: 'auto' },
              { x: 666, y: 367, type: 'frame', variant: 'auto' },
            ]}
          />
        </Animated.View>

        <OvalComponent
          state={ovalState.id}
          scale={1}
          isVisible={!isOpenReview}
        />
        <View>
          <Messages
            isOpen={!isOpenReview}
            message={message}
            progress={progress}
          />
        </View>

        <Review.Modal
          open={isOpenReview}
          close={() => setIsOpenReview(false)}
          measurement={measurement}
          onSave={() => console.log('save')}
          onRepeat={() => setIsOpenManualProposal(true)}
          onCancel={() => setIsOpenReview(false)}
          results={{
            r: {
              value: 16.67,
              type: 'automatic',
            },
            l: {
              value: 21.3,
              type: 'manual',
            },
          }}
        />

        <Instructions.Modal
          open={isOpenInstructions}
          close={() => setIsOpenInstructions(false)}
          measurement={measurement}
        />
        <Manual.Proposal
          open={isOpenManualProposal}
          onAdjust={() => setIsOpenManualContent(true)}
          onRepeat={() => setIsOpenManualProposal(false)}
          close={() => setIsOpenManualProposal(false)}
        />
        <VBOX.Modal
          open={isOpenVbox}
          vboxMm={vboxMm}
          onConfirm={(value) => {
            setVboxMm(value)
            setIsOpenVbox(false)
          }}
          onSkip={() => {
            setVboxMm(undefined)
            setIsOpenVbox(false)
          }}
          close={() => setIsOpenVbox(false)}
        />
        <View className="absolute bottom-0 left-0 z-30 gap-8 p-8">
          {isOpenControls && (
            <View className="flex flex-col gap-8">
              <Select
                value={message}
                onChange={handleMessageChange}
                options={messageOptions}
                styleOuter={{ width: 420 }}
              />
              <Segmented
                options={measurementOptions}
                value={measurementOptionById[measurement]}
                onChange={(option) => setMeasurement(option.id)}
              />
              <Segmented
                options={ovalStateOptions}
                value={ovalState}
                onChange={setOvalState}
              />
              <View
                className="flex h-12 flex-row items-center justify-center rounded-lg border border-black/10 bg-white px-6"
                style={{ width: 420 }}
              >
                <Slider
                  value={progress}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={setProgress}
                />
              </View>
            </View>
          )}
          <View className="flex flex-row gap-4">
            <VBOX.Button vboxMm={vboxMm} onPress={() => setIsOpenVbox(true)} />
            <ChipButton
              value="Instructions"
              onPress={() => setIsOpenInstructions(!isOpenInstructions)}
            />
            <Button
              size="xs"
              variant={isOpenControls ? 'filled' : 'outlined'}
              label="Controls"
              onPress={() => setIsOpenControls(!isOpenControls)}
            />
            <Button
              size="xs"
              variant={isOpenReview ? 'filled' : 'outlined'}
              label="Review"
              onPress={() => setIsOpenReview(!isOpenReview)}
            />
            <Button
              size="xs"
              variant="outlined"
              label="PD Flow"
              onPress={() =>
                openMeasurement({
                  type: 'pd',
                  onComplete: (result) =>
                    console.log('PD measurement complete', result),
                })
              }
            />
            <Button
              size="xs"
              variant="outlined"
              label="SH Flow"
              onPress={() =>
                openMeasurement({
                  type: 'sh',
                  onComplete: (result) =>
                    console.log('SH measurement complete', result),
                })
              }
            />
          </View>
        </View>
      </View>
    </>
  )
}
